import { useCallback, useContext, useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { notification } from "antd";
import {
  DIALOG_NAME,
  useDialogSearchParam,
  useOpenParamDialog,
} from "../../helpers/useDialogSearchParam";
import CustomModal, { ModalDescription, ModalTitle } from "../CustomModal";
import { AuthContext } from "../../context/AuthProvider";
import ThirdPartyAuth from "./ThirdPartyAuth";
import { Spacer, FormError } from "../../styles/CommonElements";
import styled from "styled-components";
import { device } from "../../lib/breakpoints";
import { trackEvent } from "../../helpers/mixpanel";
import ForgotPassword from "./ForgotPassword";
import useLoginMutation from "../../lib/useLoginMutation";
import Button from "../Button";
import RevealPassword from "../RevealPassword";
import { APP_NAME } from "../../helpers/constants";
import { APP_PRIVACY_ROUTE, APP_TERMS_ROUTE } from "../../helpers/routeNames";
import Logo from "../general/Logo";
import { formatEmailToLowercase } from "../../utils/emailUtils";
import { getPostLoginHomePath } from "../../helpers/accountProduct";
import { SESSION_STORAGE } from "../../helpers/constants";

const LoginModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [isPasswordRevealed, setPasswordRevealed] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const passwordDomRef = useRef(null);
  const openSignup = useOpenParamDialog(DIALOG_NAME.signup);

  const [api, contextHolder] = notification.useNotification();

  const { login, loginPending, loginError } = useLoginMutation({
    api,
  });

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState: { isDirty, isValid, errors },
  } = useForm({
    mode: "onTouched",
  });

  const { ref: rhfPasswordRef, ...passwordField } = register("password", {
    required: "Please enter your password",
  });

  const onSubmit = (data) => {
    login(data, {
      onSuccess: () => {
        reset();
        onClose();
        const redirectUrl = sessionStorage.getItem(SESSION_STORAGE.redirectUrl);
        if (redirectUrl) {
          sessionStorage.removeItem(SESSION_STORAGE.redirectUrl);
          navigate(redirectUrl, { replace: true });
          return;
        }
        navigate(getPostLoginHomePath(), { replace: true });
      },
      onError: (error) => {
        console.error("Login error:", error);
        // reset dirty status but keep values (temporarily disabling login button)
        reset({}, { keepValues: true });
      },
    });
  };

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  return (
    <>
      {contextHolder}
      <CustomModal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setForgotPassword(false);
        }}
        isCentered={true}
        width={500}
        fullScreenOnMobile
        dark
      >
        <LogoWrapper onClick={() => navigate("/")}>
          <Logo
            showTagline={false}
            size="large"
            displayMode="icon"
            hideIconFrame
            fullColorMark
          />
        </LogoWrapper>
        <Spacer $marginTop="0.5rem" />
        {!forgotPassword ? (
          <>
            <AuthEyebrow>Secure channel · {APP_NAME}</AuthEyebrow>
            <Spacer $marginTop="0.35rem" />
            <AuthTitle>Login</AuthTitle>
          </>
        ) : (
          <StyledModalTitle>Reset your password</StyledModalTitle>
        )}
        <Spacer $marginTop="0.5rem" />
        {!forgotPassword && (
          <>
            <form onSubmit={handleSubmit(onSubmit)}>
              <InputHeader htmlFor="email">Email</InputHeader>
              <Spacer $marginTop="0.25rem" />
              <StyledInput
                id="email"
                placeholder="you@email.com"
                type="email"
                {...register(
                  "email",
                  // These inline validations can be replaced by Zod
                  {
                    required: "Please enter your email address",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                      message: "Invalid email address",
                    },
                  }
                )}
                onChange={(e) => {
                  const formattedEmail = formatEmailToLowercase(e.target.value);
                  if (formattedEmail !== e.target.value) {
                    setValue("email", formattedEmail);
                  }
                }}
              />
              {errors.email && <FormError>{errors.email.message}</FormError>}
              <Spacer $marginTop="1rem" />

              <InputHeader htmlFor="password">Password</InputHeader>
              <Spacer $marginTop="0.25rem" />
              <div style={{ position: "relative" }}>
                <StyledInput
                  id="password"
                  placeholder="Enter Password"
                  type={isPasswordRevealed ? "text" : "password"}
                  {...passwordField}
                  ref={(el) => {
                    passwordDomRef.current = el;
                    rhfPasswordRef(el);
                  }}
                />
                <RevealPassword
                  isRevealed={isPasswordRevealed}
                  toggleRevealed={() =>
                    setPasswordRevealed(!isPasswordRevealed)
                  }
                  passwordRef={passwordDomRef}
                />
              </div>
              {errors.password && (
                <FormError>{errors.password.message}</FormError>
              )}
              <ForgotPasswordSection>
                <AuthInlineLink
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setForgotPassword(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setForgotPassword(true);
                    }
                  }}
                >
                  Forgot password?
                </AuthInlineLink>
              </ForgotPasswordSection>

              <Spacer $marginTop="0.5rem" />

              {loginError !== "" && <LoginError>{loginError}</LoginError>}

              <Spacer $marginTop="1rem" />

              <LuxuryLoginButton
                type="submit"
                size="large"
                disabled={!isDirty || !isValid || loginPending}
              >
                Log In
              </LuxuryLoginButton>
            </form>

            {/*
            <Spacer $marginTop="1rem" />
            <ThirdPartyAuth cta="Log In" />
            */}
            <Spacer $marginTop="1rem" />

            <Terms>
              By continuing, you agree to our{" "}
              <TermsRouterLink to={APP_TERMS_ROUTE} target="_blank">
                Terms of Service
              </TermsRouterLink>
              {" and "}
              <TermsRouterLink to={APP_PRIVACY_ROUTE} target="_blank">
                Privacy Policy
              </TermsRouterLink>
              .
            </Terms>

            <Spacer $marginTop="1rem" />
            <SignUpCta>
              Don&apos;t have an account?{" "}
              <AuthInlineLink
                role="button"
                tabIndex={0}
                onClick={() => {
                  trackEvent("Login Modal Sign Up Click", {
                    surface: "login_modal",
                  });
                  onClose();
                  openSignup();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    trackEvent("Login Modal Sign Up Click", {
                      surface: "login_modal",
                    });
                    onClose();
                    openSignup();
                  }
                }}
              >
                Sign up today.
              </AuthInlineLink>
            </SignUpCta>

            <Spacer $marginTop="0.25rem" />
          </>
        )}
        {forgotPassword && <ForgotPassword email={getValues("email")} dark />}
        <Spacer $marginTop="0.5rem" />
      </CustomModal>
    </>
  );
};

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
  margin-bottom: 1rem;
  min-height: 56px;
  width: auto;
  cursor: pointer;
`;

const ForgotPasswordSection = styled.div`
  text-align: right;
  width: 100%;
  font-size: 1rem;
  font-weight: 700;
  margin-top: 4px;
  margin-bottom: 0.35rem;
`;

/** Matches IntakeModal terms / inline link styling on dark marketing modals. */
const authModalLinkStyles = `
  color: #fb923c !important;
  font-family: var(--font-secondary);
  font-weight: 600;
  font-size: 0.75rem !important;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #fdba74 !important;
    text-decoration: underline;
  }
`;

const AuthInlineLink = styled.span`
  display: inline-block;
  cursor: pointer;
  ${authModalLinkStyles}
`;

const TermsRouterLink = styled(Link)`
  ${authModalLinkStyles}
`;

const LoginError = styled.div`
  font-family: var(--font-secondary);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.5;
  text-align: left;
  color: var(--color-error);
  margin-top: 0.5rem;
`;

const SignUpCta = styled.div`
  color: #cbd5e1;
  text-align: center;
  font-family: var(--font-secondary);
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 125%;

  span {
    cursor: pointer;
  }
`;

const LuxuryLoginButton = styled(Button)`
  background: #ff5722 !important;
  color: #ffffff !important;
  font-weight: 800;
  font-size: 1.05rem;
  border-radius: 0;
  box-shadow: 3px 3px 0 #000000;
  border: 2px solid #000000 !important;
  width: 100%;
  margin-top: 0.5rem;
  font-family: var(--font-secondary);
  letter-spacing: 0.02em;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    background: #f44e1b !important;
    color: #ffffff !important;
    transform: translate(1px, 1px);
    box-shadow: 2px 2px 0 #000000;
  }

  &:disabled {
    background: #334155 !important;
    color: #94a3b8 !important;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const StyledLogo = styled.img`
  width: 200px;
  height: auto;
`;

const StyledModalTitle = styled(ModalTitle)`
  color: #f1f5f9;
  font-size: 1.65rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 0.5rem;
  letter-spacing: -0.5px;
  font-family: var(--font-primary);

  @media ${device.mobile} {
    font-size: 1.5rem;
  }
`;

const InputHeader = styled.label`
  color: #cbd5e1;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  display: block;
  @media ${device.mobile} {
    font-size: 1rem;
  }
`;

const StyledInput = styled.input`
  width: 100%;
  font-size: 1.05rem;
  padding: 0.95rem 1.1rem;
  border-radius: 0;
  border: 2px solid #475569;
  background: #020617;
  color: #f8fafc;
  font-family: var(--font-secondary, "Inter", system-ui, sans-serif);
  margin-bottom: 0.5rem;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: #ff5722;
    box-shadow: 0 0 0 1px #ff5722, 0 0 14px rgba(255, 87, 34, 0.25);
  }

  &::placeholder {
    color: rgba(148, 163, 184, 0.85);
    font-size: 1rem;
  }
`;

const AuthEyebrow = styled.p`
  margin: 0;
  text-align: center;
  font-family: var(--font-mono, "IBM Plex Mono", ui-monospace, monospace);
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 87, 34, 0.95);
`;

const AuthTitle = styled.h1`
  margin: 0;
  text-align: center;
  font-family: var(--font-mono, "IBM Plex Mono", ui-monospace, monospace);
  font-size: clamp(0.68rem, 2vw, 0.88rem);
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 1.35;
  color: #f8fafc;
`;

const Terms = styled.p`
  color: #cbd5e1;
  text-align: center;
  font-size: 0.75rem !important;
  font-weight: 400;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  line-height: 1.5;
`;

export default LoginModal;
